import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Search, ScanBarcode, Plus, Minus, Trash2, ShoppingBag, Banknote, CreditCard, Package, Loader2, MoreHorizontal, Pencil, RefreshCcw, X,
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { ProductImageGenerateDialog } from "@/components/products/ProductImageGenerateDialog";
import { ProductArtwork } from "@/components/products/ProductArtwork";
import { InvoiceDatePicker, InvoiceField, InvoiceInput } from "@/components/sell/invoice-form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, useWorkspace } from "@/lib/store";
import { productStockStatus } from "@/lib/formatters";
import { useLocalization } from "@/lib/localization";
import { formatOperatingCurrency } from "@/lib/money";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { fetchGlobalProductCatalog, streamProductImageEvents } from "@/lib/shopApi";
import { isProductLimitError } from "@/lib/productLimits";
import { getLatestBrowserLocation } from "@/lib/location";
import { loadWebCart, loadWebGlobalCatalog, saveWebCart, saveWebGlobalCatalog } from "@/lib/offline/webOfflineStore";
import {
  buildQuickSellProduct,
  createQuickSellSchema,
  getQuickSellCopy,
  getQuickSellSuggestions,
  isValidQuickSellBarcode,
  normalizeQuickSellBarcode,
  normalizeQuickSellName,
} from "@/lib/quickSell";
import { toast } from "sonner";
import { notifyInsufficientCredits } from "@/lib/apiClient";
import { z } from "zod";
import { useTutorial } from "@/features/tutorial/TutorialContext";

function requiredNumber(value) {
  const text = String(value ?? "").trim();
  return text ? Number(text) : undefined;
}

function optionalNumber(value) {
  const text = String(value ?? "").trim();
  return text ? Number(text) : 0;
}

function createSellProductSchema(
  copy,
  stockMinimum = 0,
  stockMinimumMessage = copy.validation.stockNonNegative,
) {
  return z.object({
    barcode: z.string().trim().optional(),
    cost: z.preprocess(
      optionalNumber,
      z.number({ invalid_type_error: copy.validation.costValid }).min(0, copy.validation.costNonNegative)
    ),
    lowStockAt: z.preprocess(
      optionalNumber,
      z.number({ invalid_type_error: copy.validation.lowStockWhole })
        .int(copy.validation.lowStockWhole)
        .min(0, copy.validation.lowStockNonNegative)
    ),
    name: z.string().trim().min(1, copy.validation.productNameRequired),
    price: z.preprocess(
      requiredNumber,
      z.number({
        required_error: copy.validation.priceRequired,
        invalid_type_error: copy.validation.priceValid,
      }).positive(copy.validation.priceAboveZero)
    ),
    stock: z.preprocess(
      requiredNumber,
      z.number({
        required_error: copy.validation.stockRequired,
        invalid_type_error: copy.validation.stockWhole,
      }).int(copy.validation.stockWhole).min(stockMinimum, stockMinimumMessage)
    ),
  });
}

function createCreditSaleSchema(total, copy) {
  return z.object({
    amountPaid: z.preprocess(
      requiredNumber,
      z.number({
        required_error: copy.validation.amountPaidRequired,
        invalid_type_error: copy.validation.amountPaidValid,
      })
        .min(0, copy.validation.amountPaidNonNegative)
        .refine((value) => value < total, copy.validation.amountPaidLessThanTotal)
    ),
    customerName: z.string().trim().min(1, copy.validation.customerNameRequired),
    dueDate: z.string().trim().min(1, copy.validation.dueDateRequired),
    phone: z.string().optional(),
  });
}

function getFieldErrors(error) {
  const flattened = error.flatten().fieldErrors;
  return Object.fromEntries(
    Object.entries(flattened).map(([field, messages]) => [field, messages?.[0] || ""])
  );
}

function formatCurrencyLocal(value, locale) {
  return formatOperatingCurrency(value, locale);
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => (
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  ));

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const updateMatches = () => setMatches(mediaQuery.matches);
    updateMatches();
    mediaQuery.addEventListener("change", updateMatches);
    return () => mediaQuery.removeEventListener("change", updateMatches);
  }, [query]);

  return matches;
}

function normalizeCategoryLabel(value) {
  return String(value ?? "").trim().toLowerCase();
}

function effectiveSellableStock(product, isOnlineExecution) {
  if (isOnlineExecution) return product?.stock;
  return product?.sellableStock === undefined ? 0 : product.sellableStock;
}

function createEditableProductDraft(product) {
  return {
    barcode: product?.barcode || "",
    cost: product?.cost === undefined || product?.cost === null ? "" : String(product.cost),
    id: product?.id || "",
    lowStockAt: product?.lowStockAt === undefined || product?.lowStockAt === null ? "0" : String(product.lowStockAt),
    name: product?.name || "",
    price: product?.price === undefined || product?.price === null ? "" : String(product.price),
    status: product?.status || "Active",
    stock: product?.stock === undefined || product?.stock === null ? "0" : String(product.stock),
    stockTrackingEnabled: product?.stockTrackingEnabled !== false,
    url: product?.url || null,
    urlStatus: product?.urlStatus || "pending",
  };
}

function PremiumField({ children, error, hint, label, optional = false, optionalLabel, required = false }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label className="text-[12px] font-medium text-muted-foreground">{label}</label>
        {required ? <span className="text-[12px] font-semibold text-destructive">*</span> : null}
        {!required && optional ? <span className="text-[12px] text-muted-foreground/70">({optionalLabel})</span> : null}
      </div>
      {children}
      {hint && !error ? <div className="text-[11px] leading-4 text-muted-foreground">{hint}</div> : null}
      {error ? <div className="mt-2 text-[12px] text-destructive">{error}</div> : null}
    </div>
  );
}

export default function Sell() {
  const { session } = useAuth();
  const { cleanProductImage, products, completeSale, credits, generateProductImage, reload, saveProduct, sync } = useWorkspace();
  const { language, locale, messages } = useLocalization();
  const tutorial = useTutorial();
  const copy = messages.sell;
  const inventoryCopy = messages.inventory;
  const quickSellCopy = getQuickSellCopy(language);
  const isOnlineExecution = Boolean(sync?.online && (typeof navigator === "undefined" || navigator.onLine));
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const cartScope = [
    session?.shopId,
    session?.selectedBranchId,
    session?.userId,
    session?.authGeneration,
  ].map((value) => String(value ?? "")).join(":");
  const [cartReadyScope, setCartReadyScope] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const isCompactCheckout = useMediaQuery("(max-width: 1300px)");
  const [paymentMode, setPaymentMode] = useState("paid"); // paid | credit
  const [amountPaid, setAmountPaid] = useState("");
  const [creditCustomer, setCreditCustomer] = useState({ name: "", phone: "", dueDate: "" });
  const [createIntent, setCreateIntent] = useState(false);
  const [barcodeMode, setBarcodeMode] = useState("attach");
  const [globalProducts, setGlobalProducts] = useState([]);
  const [hasEditedProductName, setHasEditedProductName] = useState(false);
  const [newProduct, setNewProduct] = useState({ barcode: "", cost: "", lowStockAt: "3", name: "", price: "", stock: "" });
  const [productFormErrors, setProductFormErrors] = useState({});
  const [imageDialogProduct, setImageDialogProduct] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const cartPersistenceRef = useRef(Promise.resolve());
  const saleSubmissionInFlightRef = useRef(false);
  const [processingImageIds, setProcessingImageIds] = useState(() => new Set());
  const [uploadingImageIds, setUploadingImageIds] = useState(() => new Set());
  const [imageJobsByProduct, setImageJobsByProduct] = useState(() => new Map());
  const busyImageIds = useMemo(() => new Set([...processingImageIds, ...uploadingImageIds]), [processingImageIds, uploadingImageIds]);
  const productsRef = useRef(products);
  const reloadRef = useRef(reload);
  const imageMessagesRef = useRef({
    failed: messages.common.productImageGenerateFailed,
    generated: messages.common.productImageGenerated,
  });

  useEffect(() => {
    if (tutorial.active && ["cart", "payment", "customer", "phone", "due", "amount", "complete"].includes(tutorial.stage)) {
      setCheckoutOpen(true);
    }
  }, [tutorial.active, tutorial.stage]);

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    let active = true;
    setCartReadyScope("");
    setCart([]);
    loadWebCart(session)
      .then((storedCart) => {
        if (active) setCart(storedCart);
      })
      .catch(() => {
        // Checkout remains available if IndexedDB cannot restore a prior cart.
      })
      .finally(() => {
        if (active) setCartReadyScope(cartScope);
      });
    return () => {
      active = false;
    };
  }, [cartScope, session]);

  useEffect(() => {
    let active = true;
    const catalogSession = session;
    if (!catalogSession?.sessionId || !catalogSession?.selectedBranchId) {
      setGlobalProducts([]);
      return () => { active = false; };
    }

    void loadWebGlobalCatalog(catalogSession)
      .then((cached) => {
        if (active && cached.length) setGlobalProducts(cached);
      })
      .catch(() => undefined);

    if (typeof navigator === "undefined" || navigator.onLine) {
      void (async () => {
        try {
          const collected = [];
          let since = "";
          while (active) {
            const page = await fetchGlobalProductCatalog(catalogSession.sessionId, { limit: 1000, since });
            const pageProducts = Array.isArray(page.products) ? page.products : [];
            collected.push(...pageProducts);
            if (pageProducts.length < 1000 || !page.nextCursor || page.nextCursor === since) break;
            since = page.nextCursor;
          }
          if (!active) return;
          setGlobalProducts(collected);
          await saveWebGlobalCatalog(catalogSession, collected);
        } catch {
          // Cached catalogue remains available; Quick Sell itself stays local-first.
        }
      })();
    }

    return () => { active = false; };
  }, [session?.authGeneration, session?.selectedBranchId, session?.sessionId, session?.shopId, session]);

  useEffect(() => {
    if (cartReadyScope !== cartScope) return;
    const write = cartPersistenceRef.current
      .catch(() => undefined)
      .then(() => saveWebCart(session, cart));
    cartPersistenceRef.current = write;
    void write.catch((error) => {
      console.warn("[shop-state] failed to persist web cart", {
        error: error instanceof Error ? error.message : "Unknown cart persistence error",
      });
    });
  }, [cart, cartReadyScope, cartScope, session]);

  useEffect(() => {
    if (!isCompactCheckout) {
      setCheckoutOpen(false);
    }
  }, [isCompactCheckout]);

  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  useEffect(() => {
    imageMessagesRef.current = {
      failed: messages.common.productImageGenerateFailed,
      generated: messages.common.productImageGenerated,
    };
  }, [messages.common.productImageGenerateFailed, messages.common.productImageGenerated]);

  useEffect(() => {
    if (!session?.sessionId || !session?.shopId) {
      return undefined;
    }

    const controller = new AbortController();
    const storageKey = `shopbook:product-image-events:${session.shopId}:${session.userId || "user"}`;
    let cursor = Number(window.sessionStorage.getItem(storageKey) || 0) || 0;
    let reconnectAttempt = 0;
    let reloadTimer = null;
    const completedJobs = new Set();

    const applyJobs = (jobs) => {
      const nextJobs = new Map();
      const nextProcessing = new Set();
      (jobs || []).forEach((job) => {
        if (!job?.productId) return;
        if (!nextJobs.has(job.productId)) {
          nextJobs.set(job.productId, job);
        }
        if (job.status === "queued" || job.status === "processing") {
          nextProcessing.add(job.productId);
        }
      });
      setImageJobsByProduct(nextJobs);
      setProcessingImageIds(nextProcessing);
      const productsByID = new Map(productsRef.current.map((product) => [product.id, product]));
      const hasUnrefreshedCompletion = (jobs || []).some((job) => {
        if (job?.status !== "completed" || !job.imageUrl) return false;
        return productsByID.get(job.productId)?.url !== job.imageUrl;
      });
      if (hasUnrefreshedCompletion) {
        scheduleReload();
      }
    };

    const scheduleReload = () => {
      window.clearTimeout(reloadTimer);
      reloadTimer = window.setTimeout(() => {
        reloadRef.current?.().catch(() => null);
      }, 150);
    };

    const applyEvent = async ({ data, event, id }) => {
      if (id > 0) {
        cursor = Math.max(cursor, id);
        window.sessionStorage.setItem(storageKey, String(cursor));
      }
      if (event === "image.snapshot") {
        applyJobs(data?.jobs);
        return;
      }
      const productId = data?.productId;
      if (!productId) return;

      setImageJobsByProduct((previous) => {
        const next = new Map(previous);
        next.set(productId, { ...(previous.get(productId) || {}), ...data });
        return next;
      });

      const isActive = data.status === "queued" || data.status === "processing";
      setProcessingImageIds((previous) => {
        const next = new Set(previous);
        if (isActive) next.add(productId);
        else next.delete(productId);
        return next;
      });

      if (event === "image.completed" && !completedJobs.has(data.jobId)) {
        completedJobs.add(data.jobId);
        const product = productsRef.current.find((item) => item.id === productId);
        toast.success(imageMessagesRef.current.generated, { description: product?.name || "" });
        scheduleReload();
      }
      if (event === "image.failed" && !completedJobs.has(data.jobId)) {
        completedJobs.add(data.jobId);
        if (data.errorCode === "INSUFFICIENT_CREDITS") {
          notifyInsufficientCredits({ feature: "product_image", requiredUnits: 5_000 });
        } else {
          toast.error(data.errorMessage || imageMessagesRef.current.failed);
        }
        scheduleReload();
      }
    };

    const connect = async () => {
      while (!controller.signal.aborted) {
        try {
          await streamProductImageEvents(session.sessionId, {
            lastEventId: cursor,
            onEvent: applyEvent,
            signal: controller.signal,
          });
          reconnectAttempt = 0;
        } catch (error) {
          if (controller.signal.aborted || error?.name === "AbortError") return;
        }
        reconnectAttempt += 1;
        const delay = Math.min(15_000, 500 * (2 ** Math.min(reconnectAttempt, 5)));
        await new Promise((resolve) => window.setTimeout(resolve, delay + Math.random() * 250));
      }
    };

    void connect();
    return () => {
      controller.abort();
      window.clearTimeout(reloadTimer);
    };
  }, [session?.sessionId, session?.shopId, session?.userId]);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const [isRefreshingProducts, setIsRefreshingProducts] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductDraft, setEditProductDraft] = useState(null);
  const [editProductErrors, setEditProductErrors] = useState({});
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);
  const normalizedQuery = normalizeQuickSellName(query);
  const normalizedGeneralCategory = normalizeCategoryLabel(messages.common.general);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["all", ...Array.from(set)];
  }, [products]);

  const isRefreshCategory = useCallback((value) => {
    const normalized = normalizeCategoryLabel(value);
    return normalized === "general" || normalized === normalizedGeneralCategory;
  }, [normalizedGeneralCategory]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (category !== "all" && p.category !== category) return false;
      if (!query.trim()) return !createIntent && !newProduct.barcode;
      return normalizeQuickSellName(p.name).includes(normalizedQuery) ||
             (p.barcode && normalizeQuickSellBarcode(p.barcode).includes(normalizeQuickSellBarcode(query)));
    });
  }, [products, query, normalizedQuery, category, createIntent, newProduct.barcode]);

  const quickSuggestions = useMemo(
    () => getQuickSellSuggestions(products, globalProducts, query),
    [globalProducts, products, query],
  );
  const localSuggestions = quickSuggestions.local;
  const globalSuggestions = newProduct.barcode && barcodeMode !== "create"
    ? []
    : quickSuggestions.global;

  const exactProduct = useMemo(() => {
    if (!normalizedQuery) return null;
    return products.find((product) => (
      normalizeQuickSellName(product.name) === normalizedQuery ||
      normalizeQuickSellBarcode(product.barcode) === normalizeQuickSellBarcode(query)
    )) ?? null;
  }, [normalizedQuery, products, query]);

  const isSearching = query.trim().length > 0 || createIntent || Boolean(newProduct.barcode);
  const canCreateInline = query.trim().length > 0
    && !exactProduct
    && (!newProduct.barcode || barcodeMode === "create");
  const canSaveQuickProduct = Boolean(String(newProduct.name || query).trim())
    && Number.isFinite(Number(newProduct.price))
    && Number(newProduct.price) > 0;

  const total = cart.reduce((acc, c) => acc + c.unitPrice * c.quantity, 0);
  const paid = paymentMode === "paid" ? total : Number(amountPaid || 0);
  const balanceDue = Math.max(0, total - paid);

  const add = (p) => {
    const availableStock = effectiveSellableStock(p, isOnlineExecution);
    if (availableStock === 0) {
      toast.error(copy.productOutOfStock(p.name));
      return;
    }
    setCart((prev) => {
      const exists = prev.find((c) => c.productId === p.id);
        if (exists) {
          if (availableStock !== null && exists.quantity >= availableStock) {
          toast.error(copy.productOnlyInStock(p.name, availableStock));
          return prev;
        }
        return prev.map((c) => c.productId === p.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, {
        cartId: `cart_${p.id}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        productId: p.id,
        name: p.name,
        quantity: 1,
        unitPrice: p.price,
        stock: availableStock,
      }];
    });
    if (tutorial.active && ["existing", "add"].includes(tutorial.stage)) {
      tutorial.setStage("cart");
    }
  };

  const updateQty = (id, delta) => {
    setCart((prev) =>
      prev.flatMap((c) => {
        if (c.productId !== id) return [c];
        const next = c.quantity + delta;
        if (delta > 0 && c.stock !== null && next > c.stock) {
          toast.error(copy.productOnlyInStock(c.name, c.stock));
          return [c];
        }
        return next <= 0 ? [] : [{ ...c, quantity: next }];
      })
    );
  };

  const updatePrice = (id, val) => {
    setCart((prev) => prev.map((c) => c.productId === id ? { ...c, unitPrice: Number(val) || 0 } : c));
  };

  const remove = (id) => setCart((prev) => prev.filter((c) => c.productId !== id));
  const clear = () => {
    setCart([]);
    setAmountPaid("");
    setCreditCustomer({ name: "", phone: "", dueDate: "" });
  };

  const openImageDialog = (product) => {
    if (!product) {
      return;
    }
    setImageDialogProduct(product);
  };

  const openEditProductDialog = (product) => {
    if (!product) {
      return;
    }
    setEditingProduct(product);
    setEditProductDraft(createEditableProductDraft(product));
    setEditProductErrors({});
  };

  const closeEditProductDialog = () => {
    if (isUpdatingProduct) {
      return;
    }
    setEditingProduct(null);
    setEditProductDraft(null);
    setEditProductErrors({});
  };

  const handleGenerateProductImage = async ({ description = "", image = null, mode = "generate" } = {}) => {
    if (!imageDialogProduct || busyImageIds.has(imageDialogProduct.id)) {
      return;
    }

    const targetProduct = imageDialogProduct;
    const trimmedDescription = String(description || "").trim();
    setImageDialogProduct(null);
    const setBusyIds = mode === "upload" ? setUploadingImageIds : setProcessingImageIds;
    setBusyIds((prev) => {
      const next = new Set(prev);
      next.add(targetProduct.id);
      return next;
    });

    void (async () => {
      try {
        if (mode === "upload") {
          await cleanProductImage(targetProduct.id, image, trimmedDescription);
          toast.success(messages.common.productImageGenerated, { description: targetProduct.name });
          setUploadingImageIds((prev) => {
            const next = new Set(prev);
            next.delete(targetProduct.id);
            return next;
          });
        } else {
          const job = await generateProductImage(targetProduct.id, {
            description: trimmedDescription || undefined,
            force: Boolean(targetProduct.url),
          });
          if (job) {
            setImageJobsByProduct((previous) => {
              const next = new Map(previous);
              const existing = previous.get(targetProduct.id);
              if (existing?.status === "completed" || existing?.status === "failed") return previous;
              next.set(targetProduct.id, job);
              return next;
            });
          }
        }
      } catch (error) {
        setProcessingImageIds((prev) => {
          const next = new Set(prev);
          next.delete(targetProduct.id);
          return next;
        });
        setUploadingImageIds((prev) => {
          const next = new Set(prev);
          next.delete(targetProduct.id);
          return next;
        });
        if (error?.code === "INSUFFICIENT_CREDITS") {
          notifyInsufficientCredits({ feature: "product_image", requiredUnits: 5_000 });
        } else {
          toast.error(error instanceof Error ? error.message : messages.common.productImageGenerateFailed);
        }
      }
    })();
  };

  const resetProductDraft = () => {
    setNewProduct({ barcode: "", cost: "", lowStockAt: "3", name: "", price: "", stock: "" });
    setProductFormErrors({});
    setHasEditedProductName(false);
    setCreateIntent(false);
    setBarcodeMode("attach");
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    if (!hasEditedProductName) {
      setNewProduct((prev) => ({ ...prev, name: value }));
    }
    setProductFormErrors({});
    if (!value.trim()) {
      setCreateIntent(false);
      setHasEditedProductName(false);
      setNewProduct((prev) => ({ ...prev, name: "" }));
    }
  };

  const updateProductDraft = (field, value) => {
    setNewProduct((prev) => ({ ...prev, [field]: value }));
    setProductFormErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const updateEditProductDraft = (field, value) => {
    setEditProductDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    setEditProductErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const openNewProduct = (name = "", barcode = "") => {
    const nextName = name.trim();
    if (!nextName) {
      document.querySelector("[data-testid='sell-search']")?.focus();
      toast.info(copy.keepTyping);
      return;
    }
    setNewProduct({ barcode, cost: "", lowStockAt: "3", name, price: "", stock: "" });
    setProductFormErrors({});
    setQuery(nextName);
    setCreateIntent(true);
    setHasEditedProductName(Boolean(nextName));
    setBarcodeMode(barcode ? "create" : "attach");
  };

  const clearPendingBarcode = () => {
    updateProductDraft("barcode", "");
    setBarcodeMode("attach");
  };

  const selectGlobalProduct = (product) => {
    const name = String(product?.name || "").trim();
    if (!name) return;
    setQuery(name);
    setNewProduct((previous) => ({ ...previous, name, price: "" }));
    setProductFormErrors({});
    setCreateIntent(true);
    setHasEditedProductName(true);
    if (newProduct.barcode) setBarcodeMode("create");
  };

  useBarcodeScanner({
    onScan: (barcode) => {
      const code = normalizeQuickSellBarcode(barcode);
      if (!isValidQuickSellBarcode(code)) {
        toast.error(quickSellCopy.numericBarcode);
        return;
      }
      if (editingProduct) {
        updateEditProductDraft("barcode", code);
        toast.success(copy.barcodeAttached, { description: code });
        return;
      }
      const matched = products.find((product) => String(product.barcode || "").trim() === code);
      if (matched) {
        add(matched);
        setQuery("");
        resetProductDraft();
        toast.success(copy.productScanned, { description: matched.name });
        return;
      }

      if (createIntent && String(newProduct.name || query).trim()) {
        updateProductDraft("barcode", code);
        setBarcodeMode("create");
        toast.success(copy.barcodeAttached, { description: code });
        return;
      }

      // Keep an unmatched scan as the optional barcode, but require a human-readable
      // product hint before Quick Sell can create the item.
      setQuery("");
      setNewProduct({ barcode: code, cost: "", lowStockAt: "3", name: "", price: "", stock: "" });
      setProductFormErrors({});
      setCreateIntent(false);
      setHasEditedProductName(false);
      setBarcodeMode("attach");
      toast.info(copy.barcodeNotFound, { description: copy.barcodeNotFoundDescription });
    },
  }, [copy.barcodeAttached, copy.barcodeNotFound, copy.barcodeNotFoundDescription, copy.productScanned, createIntent, editingProduct, newProduct.name, products, query, quickSellCopy.numericBarcode, updateEditProductDraft]);

  const createProduct = async () => {
    if (isSavingProduct) {
      return;
    }

    const parsed = createQuickSellSchema(copy).safeParse({
      barcode: newProduct.barcode,
      name: newProduct.name || query,
      price: newProduct.price,
    });
    if (!parsed.success) {
      setProductFormErrors(getFieldErrors(parsed.error));
      toast.error(copy.checkRequiredProductDetails);
      return;
    }

    setProductFormErrors({});

    setIsSavingProduct(true);
    try {
      const product = await saveProduct(buildQuickSellProduct(parsed.data));
      add(product);
      toast.success(copy.productAddedToCart, { description: product.name });
      setQuery("");
      resetProductDraft();
    } catch (error) {
      if (isProductLimitError(error)) {
        return;
      }
      toast.error(copy.saveProductFailed, {
        description: error instanceof Error ? error.message : copy.tryAgain,
      });
    } finally {
      setIsSavingProduct(false);
    }
  };

  const complete = async () => {
    if (cart.length === 0 || saleSubmissionInFlightRef.current) return;
    for (const saleItem of cart) {
      const currentProduct = products.find((product) => product.id === saleItem.productId);
      const availableStock = effectiveSellableStock(currentProduct, isOnlineExecution);
      if (
        currentProduct &&
        availableStock !== null &&
        saleItem.quantity > availableStock
      ) {
        toast.error(copy.productOnlyInStock(currentProduct.name, availableStock));
        return;
      }
    }
    let salePaid = total;
    let saleBalance = 0;

    if (paymentMode === "credit") {
      const parsed = createCreditSaleSchema(total, copy).safeParse({
        amountPaid,
        customerName: creditCustomer.name,
        dueDate: creditCustomer.dueDate,
        phone: creditCustomer.phone,
      });

      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message || copy.validation.invalidCreditAmount);
        return;
      }

      salePaid = parsed.data.amountPaid;
      saleBalance = Math.max(0, total - salePaid);
    }

    saleSubmissionInFlightRef.current = true;
    setIsCompleting(true);
    const startedAt = Date.now();
    let locationCompletedAt = null;
    let persistenceCompletedAt = null;
    let outcome = "failed";
    const paymentStatus = paymentMode === "paid" ? "Paid" : (salePaid === 0 ? "Unpaid" : "Partly paid");

    try {
      const location = getLatestBrowserLocation();
      if (location) locationCompletedAt = Date.now();
      await cartPersistenceRef.current.catch(() => undefined);

      const saleId = `s_${Date.now()}`;
      const debtId = paymentMode === "credit" ? `d_${Date.now()}` : null;
      const sale = {
        id: saleId,
        idempotencyKey: `idem_${saleId}`,
        createdAt: new Date().toISOString(),
        cashier: `${session?.shopName?.split(" ")[0] || inventoryCopy.owner} (${session?.role})`,
        total,
        amountPaid: salePaid,
        balanceDue: saleBalance,
        paymentStatus,
        syncState: "Synced",
        items: cart,
        debtId,
        location,
      };
      if (debtId) {
        sale.debt = {
          amountOwed: total,
          amountPaid: salePaid,
          branchId: session?.selectedBranchId || "",
          customerName: creditCustomer.name.trim(),
          dueDate: creditCustomer.dueDate,
          dueDateIso: creditCustomer.dueDate ? new Date(`${creditCustomer.dueDate}T12:00:00`).toISOString() : "",
          id: debtId,
          idempotencyKey: `idem_${debtId}`,
          itemSummary: cart.map((item) => `${item.quantity}x ${item.name}`).join(", "),
          phone: creditCustomer.phone.trim(),
          saleId,
          status: paymentStatus,
        };
      }
      await completeSale(sale);
      persistenceCompletedAt = Date.now();
      outcome = "saved";
      if (tutorial.active && tutorial.progress?.currentStep === "first_sale") {
        await tutorial.saleCompleted({ productId: cart[0]?.productId, saleId });
      }
      toast.success(copy.saleRecorded, {
        description: copy.saleRecordedSummary(formatCurrencyLocal(total, locale), cart.length),
      });
      clear();
      setCheckoutOpen(false);
    } catch (error) {
      toast.error(copy.recordSaleFailed, {
        description: error instanceof Error ? error.message : copy.refreshAndTryAgain,
      });
    } finally {
      console.info("[checkout-performance]", {
        kind: paymentMode === "credit" ? "credit" : "cash",
        locationMs: locationCompletedAt ? locationCompletedAt - startedAt : null,
        outcome,
        persistenceMs: locationCompletedAt && persistenceCompletedAt
          ? persistenceCompletedAt - locationCompletedAt
          : null,
        totalMs: Date.now() - startedAt,
      });
      saleSubmissionInFlightRef.current = false;
      setIsCompleting(false);
    }
  };

  const addFromFinder = (product) => {
    add(product);
    setQuery("");
    resetProductDraft();
  };

  const selectLocalSuggestion = async (product) => {
    if (!newProduct.barcode) {
      addFromFinder(product);
      return;
    }
    if (isSavingProduct) return;

    setIsSavingProduct(true);
    try {
      const updated = await saveProduct({
        ...product,
        barcode: newProduct.barcode,
        stockTrackingEnabled: product.stockTrackingEnabled !== false && product.stock !== null,
      });
      add(updated);
      toast.success(copy.barcodeAttached, { description: updated.name });
      setQuery("");
      resetProductDraft();
    } catch (error) {
      toast.error(copy.saveProductFailed, {
        description: error instanceof Error ? error.message : copy.tryAgain,
      });
    } finally {
      setIsSavingProduct(false);
    }
  };

  const cancelCreateProduct = () => {
    setQuery("");
    resetProductDraft();
    document.querySelector("[data-testid='sell-search']")?.focus();
  };

  const refreshProducts = async () => {
    if (isRefreshingProducts) {
      return;
    }
    setIsRefreshingProducts(true);
    try {
      await reload();
      toast.success(copy.refreshProductsDone);
    } catch (error) {
      toast.error(copy.refreshProductsFailed, {
        description: error instanceof Error ? error.message : copy.refreshProductsHint,
      });
    } finally {
      setIsRefreshingProducts(false);
    }
  };

  const saveEditedProduct = async () => {
    if (!editingProduct || !editProductDraft || isUpdatingProduct) {
      return;
    }

    const parsed = createSellProductSchema(copy).safeParse(editProductDraft);
    if (!parsed.success) {
      setEditProductErrors(getFieldErrors(parsed.error));
      toast.error(copy.checkRequiredProductDetails);
      return;
    }

    setEditProductErrors({});
    setIsUpdatingProduct(true);
    try {
      await saveProduct(
        {
          barcode: parsed.data.barcode || null,
          cost: parsed.data.cost,
          id: editingProduct.id,
          lowStockAt: parsed.data.lowStockAt,
          name: parsed.data.name,
          price: parsed.data.price,
          status: editingProduct.status || "Active",
          stock: parsed.data.stock,
          stockTrackingEnabled: true,
          url: editingProduct.url || null,
          urlStatus: editingProduct.urlStatus || "pending",
        },
        {
          stockDelta: parsed.data.stock - Number(editingProduct.stock || 0),
        }
      );
      toast.success(inventoryCopy.productUpdated, { description: parsed.data.name });
      closeEditProductDialog();
    } catch (error) {
      toast.error(copy.saveProductFailed, {
        description: error instanceof Error ? error.message : copy.tryAgain,
      });
    } finally {
      setIsUpdatingProduct(false);
    }
  };

  return (
    <div
      aria-busy={isCompleting}
      className={`flex h-full min-h-0 flex-col lg:flex-row lg:overflow-hidden ${isCompleting ? "pointer-events-none" : ""}`}
    >
      {/* Products grid */}
      <div className="flex-1 min-h-0">
        <PageContainer className="!max-w-none flex h-full min-h-0 flex-col">
          {/* Search */}
          <div className="flex items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="sell-search"
                data-barcode-input="true"
                autoFocus
                placeholder={copy.searchPlaceholder}
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                className="pl-10 pr-12 h-11 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-border focus-visible:shadow-[0_14px_34px_-24px_rgba(15,23,42,0.35)]"
              />
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent transition-colors"
                data-testid="sell-scan-barcode"
                onClick={() => {
                  toast.info(copy.scannerReady);
                  document.querySelector("[data-testid='sell-search']")?.focus();
                }}
                title={copy.scannerReady}
              >
                <ScanBarcode className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="group relative h-11 w-auto shrink-0 gap-1.5 overflow-hidden rounded-xl border border-primary/20 bg-[linear-gradient(135deg,hsl(var(--background))_0%,hsl(var(--primary)/0.1)_42%,hsl(var(--primary)/0.16)_100%)] px-3.5 text-[12px] font-semibold text-foreground shadow-[0_20px_44px_-28px_rgba(15,23,42,0.48)] ring-1 ring-white/55 transition-all duration-300 hover:-translate-y-px hover:border-primary/35 hover:shadow-[0_26px_56px_-28px_rgba(15,23,42,0.58)] hover:ring-white/70 focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0"
              data-testid="sell-create-product"
              onClick={() => openNewProduct(query)}
            >
              <span className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent opacity-90" />
              <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/65 bg-white/70 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_10px_18px_-14px_rgba(15,23,42,0.6)] transition-transform duration-300 group-hover:scale-105 group-hover:bg-white/80">
                <Plus className="h-3.5 w-3.5" />
              </span>
              <span className="tracking-[0.01em]">{copy.newProduct}</span>
            </Button>
          </div>

          {/* Categories */}
          {!isSearching && (
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
            {categories.map((c) => (
              isRefreshCategory(c) ? (
                <button
                  key={c}
                  type="button"
                  data-testid="sell-refresh-products"
                  onClick={refreshProducts}
                  disabled={isRefreshingProducts}
                  title={copy.refreshProductsHint}
                  className="px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wider border transition-all whitespace-nowrap border-border bg-card hover:border-foreground/30 disabled:cursor-wait disabled:opacity-80"
                >
                  {copy.refreshProducts}
                </button>
              ) : (
                <button
                  key={c}
                  data-testid={`sell-category-${c}`}
                  onClick={() => setCategory(c)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wider border transition-all whitespace-nowrap
                    ${category === c
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card hover:border-foreground/30"
                    }`}
                >
                  {c === "all" ? copy.allProducts : c}
                </button>
              )
            ))}
          </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto pr-4 scrollbar-hover">
          {isSearching ? (
            <div className="mx-auto max-w-3xl rounded-lg border border-border/70 bg-card shadow-[0_28px_70px_-36px_rgba(15,23,42,0.38)]">
              <div className="border-b border-border/45 px-4 py-3">
                <div className="text-sm font-semibold">{copy.addNewProduct}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {copy.addNewProductDescription}
                </div>
              </div>

              {newProduct.barcode ? (
                <div className="border-b border-border/60 bg-muted/20 px-4 py-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-foreground">{quickSellCopy.notLinked}</div>
                      <div className="mt-1 truncate font-mono text-xs text-muted-foreground">{newProduct.barcode}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex rounded-full border border-border/70 bg-background p-1">
                        {[{ label: quickSellCopy.attach, value: "attach" }, { label: quickSellCopy.createNew, value: "create" }].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setBarcodeMode(option.value)}
                            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${barcodeMode === option.value ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={clearPendingBarcode} className="h-8 w-8 rounded-full" aria-label={copy.clear} title={copy.clear}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {barcodeMode === "create" ? quickSellCopy.barcodeSaved : quickSellCopy.useOrCancel}
                  </p>
                </div>
              ) : null}

              {(localSuggestions.length > 0 || globalSuggestions.length > 0) && (
                <div className="divide-y divide-border">
                  {localSuggestions.map((p) => {
                    const status = productStockStatus(p);
                    const isOut = status === "stock_out";
                    return (
                      <div
                        key={p.id}
                        data-testid={`sell-product-result-${p.id}`}
                        role="button"
                        tabIndex={isOut ? -1 : 0}
                        onClick={() => {
                          if (!isOut) {
                            void selectLocalSuggestion(p);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (isOut) {
                            return;
                          }
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            void selectLocalSuggestion(p);
                          }
                        }}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors
                          ${isOut ? "cursor-not-allowed opacity-50" : "hover:bg-accent/70"}`}
                      >
                        <ProductArtwork
                          compact
                          imageGenerationPhase={imageJobsByProduct.get(p.id)?.phase || ""}
                          isGeneratingImage={busyImageIds.has(p.id)}
                          onGenerateImage={openImageDialog}
                          product={p}
                          className="h-10 w-10 shrink-0 rounded-md"
                          imageClassName="rounded-md"
                          iconClassName="h-5 w-5 text-muted-foreground/70"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold">{p.name}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span className="uppercase tracking-wider">{p.category}</span>
                            {p.barcode ? <span className="font-mono">{p.barcode}</span> : null}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-sm font-semibold tabular-nums">{formatCurrencyLocal(p.price, locale)}</div>
                          <div className="mt-1"><StockBadge copy={copy} status={status} stock={p.stock} /></div>
                        </div>
                        <div className="shrink-0">
                          <ProductCardMenu
                            copy={copy}
                            disabled={busyImageIds.has(p.id)}
                            onEdit={() => openEditProductDialog(p)}
                            onRegenerateImage={() => openImageDialog(p)}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {globalSuggestions.map((product) => (
                    <button
                      key={`global-${product.id}`}
                      type="button"
                      onClick={() => selectGlobalProduct(product)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/70"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Package className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{product.name}</span>
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                          {[product.brand, product.packageSize].filter(Boolean).join(" · ") || quickSellCopy.catalogItem}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs font-semibold text-primary">{quickSellCopy.setPrice}</span>
                    </button>
                  ))}
                </div>
              )}

              {canCreateInline && (
                <div className="border-t border-border bg-background px-4 py-4">
                  <div className="grid gap-3">
                    <div className="rounded-xl border border-primary/15 bg-primary/[0.045] px-4 py-3.5">
                      <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <ShoppingBag className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {quickSellCopy.title(String(newProduct.name || query).trim())}
                          </p>
                          <p className="mt-1 text-[12px] leading-5 text-muted-foreground">{quickSellCopy.intro}</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(220px,0.75fr)]">
                      <PremiumField label={copy.sellingPrice} error={productFormErrors.price} required>
                        <Input
                          data-testid="sell-inline-product-price"
                          placeholder="0"
                          inputMode="numeric"
                          value={newProduct.price}
                          onChange={(e) => updateProductDraft("price", e.target.value.replace(/[^0-9.]/g, ""))}
                          className="h-10 rounded-lg border-border/70 bg-background font-mono text-[12px] font-semibold tabular-nums shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                      </PremiumField>
                      <button
                        type="button"
                        data-testid="sell-inline-scan-barcode"
                        onClick={() => {
                          toast.info(copy.scannerReady, { description: copy.scannerReadyDescription });
                          document.querySelector("[data-testid='sell-search']")?.focus();
                        }}
                        className="flex min-h-[66px] items-center gap-3 rounded-lg border border-border/70 bg-muted/30 px-3 text-left transition-colors hover:border-primary/25 hover:bg-primary/[0.035] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background text-primary shadow-sm">
                          <ScanBarcode className="h-4 w-4" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12px] font-medium">{copy.optionalBarcode}</span>
                          <span className="block truncate text-[12px] text-muted-foreground">
                            {newProduct.barcode ? newProduct.barcode : copy.scanToAttach}
                          </span>
                        </span>
                      </button>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        data-testid="sell-inline-cancel-product"
                        onClick={cancelCreateProduct}
                        disabled={isSavingProduct}
                        className="h-9"
                      >
                        {copy.cancel}
                      </Button>
                      <Button
                        data-testid="sell-inline-create-product"
                        onClick={createProduct}
                        disabled={isSavingProduct || !canSaveQuickProduct}
                        className="h-9 justify-center gap-2"
                      >
                        {isSavingProduct ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        {isSavingProduct ? copy.saving : quickSellCopy.addToSale}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {!localSuggestions.length && !globalSuggestions.length && !canCreateInline && (
                <div className="px-4 py-10 text-center text-muted-foreground">
                  <Package className="mx-auto mb-3 h-10 w-10 opacity-40" />
                  <div className="text-sm">{copy.keepTyping}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] items-start gap-4">
              {filtered.map((p) => {
                const status = productStockStatus(p);
                const isOut = status === "stock_out";
                const formattedPrice = formatCurrencyLocal(p.price, locale);
                return (
                  <div
                    key={p.id}
                    data-testid={`sell-product-${p.id}`}
                    onClick={() => add(p)}
                    role="button"
                    tabIndex={isOut ? -1 : 0}
                    onKeyDown={(event) => {
                      if (isOut) {
                        return;
                      }
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        add(p);
                      }
                    }}
                    className={`group relative text-left rounded-lg border border-border bg-card p-3 transition-all
                      ${isOut ? "opacity-50 cursor-not-allowed" : "hover:border-primary/40 hover:-translate-y-px hover:shadow-sm"}`}
                  >
                    <div className="absolute right-2 top-2 z-10">
                      <ProductCardMenu
                        copy={copy}
                        disabled={busyImageIds.has(p.id)}
                        onEdit={() => openEditProductDialog(p)}
                        onRegenerateImage={() => openImageDialog(p)}
                      />
                    </div>
                    <ProductArtwork
                      imageGenerationPhase={imageJobsByProduct.get(p.id)?.phase || ""}
                      isGeneratingImage={busyImageIds.has(p.id)}
                      onGenerateImage={openImageDialog}
                      product={p}
                      className="mb-2 aspect-[16/10] w-full rounded-md"
                      imageClassName="rounded-md"
                      iconClassName="h-6 w-6 text-muted-foreground/60"
                    />
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1 truncate text-[11px] uppercase tracking-wider text-muted-foreground">
                        {p.category}
                      </div>
                      <StockBadge copy={copy} status={status} stock={p.stock} />
                    </div>
                    <div className="text-[13px] font-semibold mt-0.5 truncate">{p.name}</div>
                    <div className="mt-1.5 min-w-0" title={formattedPrice}>
                      <div className="truncate font-mono text-sm font-semibold tracking-[-0.02em] tabular-nums">
                        {formattedPrice}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full text-center py-16 text-muted-foreground">
                  <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <div className="text-sm">{copy.noProductsAvailableYet}</div>
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => openNewProduct("")}>
                    <Plus className="h-4 w-4 mr-1" /> {copy.addFirstProduct}
                  </Button>
                </div>
              )}
            </div>
          )}
          </div>
        </PageContainer>
      </div>

      {/* Cart panel */}
      <ResponsiveCheckout
        cartCount={cart.length}
        cartLabel={copy.cart}
        compact={isCompactCheckout}
        formattedTotal={formatCurrencyLocal(total, locale)}
        onOpenChange={setCheckoutOpen}
        open={checkoutOpen}
      >
        <div className="checkout-panel-header px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-3.5 w-3.5 text-primary" />
            <span className="font-semibold tracking-tight">{copy.cart}</span>
            <Badge variant="secondary" className="font-mono text-[10px] h-5 px-1.5">{cart.length}</Badge>
          </div>
          {cart.length > 0 && (
            <button data-testid="sell-clear-cart" onClick={clear} className="text-xs text-muted-foreground hover:text-destructive transition-colors">
              {copy.clear}
            </button>
          )}
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          {cart.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              <div className="h-12 w-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
                <ShoppingBag className="h-5 w-5 text-muted-foreground/60" />
              </div>
              <div className="font-semibold tracking-tight">{copy.cartEmpty}</div>
              <div className="text-sm text-muted-foreground mt-1 max-w-[240px]">
                {copy.cartEmptyDescription}
              </div>
            </div>
          ) : (
            <div className="flex-1 divide-y divide-border overflow-y-auto scrollbar-hover">
              {cart.map((item) => (
                <div key={item.productId} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{item.name}</div>
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          data-testid={`cart-decrement-${item.productId}`}
                          onClick={() => updateQty(item.productId, -1)}
                          className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-input hover:bg-accent"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="font-mono tabular-nums w-6 text-center">{item.quantity}</span>
                        <button
                          data-testid={`cart-increment-${item.productId}`}
                          onClick={() => updateQty(item.productId, 1)}
                          className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-input hover:bg-accent"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                        <div className="ml-3 inline-flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">@</span>
                          <input
                            data-testid={`cart-price-${item.productId}`}
                            value={item.unitPrice}
                            onChange={(e) => updatePrice(item.productId, e.target.value)}
                            className="w-20 h-7 px-2 rounded-md border border-input bg-background font-mono tabular-nums"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-semibold tabular-nums">
                        {formatCurrencyLocal(item.unitPrice * item.quantity, locale)}
                      </div>
                      <button
                        data-testid={`cart-remove-${item.productId}`}
                        onClick={() => remove(item.productId)}
                        className="mt-2 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-5 space-y-4">
          {/* Payment mode */}
          <Tabs value={paymentMode} onValueChange={setPaymentMode}>
            <TabsList data-testid="sell-payment-methods" className="w-full grid grid-cols-2 text-[12px]">
              <TabsTrigger data-testid="sell-payment-paid" value="paid" className="gap-2">
                <Banknote className="h-3.5 w-3.5" /> {copy.paid}
              </TabsTrigger>
              <TabsTrigger data-testid="sell-payment-credit" value="credit" className="gap-2">
                <CreditCard className="h-3.5 w-3.5" /> {copy.onCredit}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {paymentMode === "credit" && (
            <div className="space-y-3">
              <InvoiceField label={copy.customerName}>
                <InvoiceInput
                  data-testid="sell-credit-customer"
                  placeholder={copy.customerNamePlaceholder}
                  value={creditCustomer.name}
                  onChange={(e) => setCreditCustomer((prev) => ({ ...prev, name: e.target.value }))}
                />
              </InvoiceField>
              <div className="grid grid-cols-2 gap-2">
                <InvoiceField label={copy.phone}>
                  <InvoiceInput
                    data-testid="sell-credit-phone"
                    placeholder={copy.optional}
                    value={creditCustomer.phone}
                    onChange={(e) => setCreditCustomer((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                </InvoiceField>
                <InvoiceField label={copy.dueDate}>
                  <InvoiceDatePicker
                    data-testid="sell-credit-due-date"
                    value={creditCustomer.dueDate}
                    onChange={(value) => setCreditCustomer((prev) => ({ ...prev, dueDate: value }))}
                  />
                </InvoiceField>
              </div>
              <InvoiceField label={copy.amountPaidNow}>
                <InvoiceInput
                  data-testid="sell-amount-paid"
                  placeholder="0"
                  inputMode="numeric"
                  value={amountPaid}
                  onChange={(e) => {
                    const nextValue = e.target.value.replace(/[^0-9]/g, "");
                    if (nextValue && total > 0 && Number(nextValue) >= total) return;
                    setAmountPaid(nextValue);
                  }}
                  className="font-mono tabular-nums"
                />
              </InvoiceField>
              <div className="text-[12px] text-muted-foreground flex justify-between">
                <span>{copy.balanceDue}</span>
                <span className="font-mono tabular-nums text-foreground font-medium">{formatCurrencyLocal(balanceDue, locale)}</span>
              </div>
            </div>
          )}

          <div className="flex items-baseline justify-between">
            <span className="text-[12px] text-muted-foreground">{copy.total}</span>
            <span data-testid="sell-cart-total" className="font-mono text-xl font-semibold tabular-nums">{formatCurrencyLocal(total, locale)}</span>
          </div>

          <Button
            data-testid="sell-complete-sale"
            disabled={cart.length === 0 || isCompleting}
            onClick={complete}
            size="lg"
            className="w-full h-11"
          >
            {isCompleting ? copy.recording : paymentMode === "paid" ? copy.completeSale : copy.recordCreditSale}
          </Button>
        </div>
      </ResponsiveCheckout>

      <ProductImageGenerateDialog
        open={Boolean(imageDialogProduct)}
        onOpenChange={(open) => {
          if (!open) {
            setImageDialogProduct(null);
          }
        }}
        onConfirm={handleGenerateProductImage}
        product={imageDialogProduct}
        balance={credits}
        generating={Boolean(imageDialogProduct && busyImageIds.has(imageDialogProduct.id))}
      />
      <Dialog open={Boolean(editingProduct)} onOpenChange={(open) => !open && closeEditProductDialog()}>
        <DialogContent className="max-w-xl border-border/70 p-6 shadow-2xl">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle>{inventoryCopy.editProduct}</DialogTitle>
            <DialogDescription>{inventoryCopy.formDescription}</DialogDescription>
          </DialogHeader>
          {editProductDraft ? (
            <div className="grid gap-3">
              <PremiumField label={copy.productName} error={editProductErrors.name} optionalLabel={copy.optionalShort}>
                <Input
                  data-testid="sell-edit-product-name"
                  placeholder={copy.productNamePlaceholder}
                  value={editProductDraft.name}
                  onChange={(e) => updateEditProductDraft("name", e.target.value)}
                  className="h-10 rounded-lg border-border/70 bg-background text-[12px] font-medium shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </PremiumField>
              <div className="grid grid-cols-2 gap-3">
                <PremiumField label={copy.costPrice} optional optionalLabel={copy.optionalShort} error={editProductErrors.cost}>
                  <Input
                    data-testid="sell-edit-product-cost"
                    placeholder="0"
                    inputMode="numeric"
                    value={editProductDraft.cost}
                    onChange={(e) => updateEditProductDraft("cost", e.target.value.replace(/[^0-9.]/g, ""))}
                    className="h-10 rounded-lg border-border/70 bg-background font-mono text-[12px] font-semibold tabular-nums shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </PremiumField>
                <PremiumField label={copy.sellingPrice} optionalLabel={copy.optionalShort} error={editProductErrors.price}>
                  <Input
                    data-testid="sell-edit-product-price"
                    placeholder="0"
                    inputMode="numeric"
                    value={editProductDraft.price}
                    onChange={(e) => updateEditProductDraft("price", e.target.value.replace(/[^0-9.]/g, ""))}
                    className="h-10 rounded-lg border-border/70 bg-background font-mono text-[12px] font-semibold tabular-nums shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </PremiumField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <PremiumField label={copy.openingStock} optionalLabel={copy.optionalShort} error={editProductErrors.stock}>
                  <Input
                    data-testid="sell-edit-product-stock"
                    placeholder="0"
                    inputMode="numeric"
                    value={editProductDraft.stock}
                    onChange={(e) => updateEditProductDraft("stock", e.target.value.replace(/[^0-9]/g, ""))}
                    className="h-10 rounded-lg border-border/70 bg-background font-mono text-[12px] font-semibold tabular-nums shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </PremiumField>
                <PremiumField label={inventoryCopy.lowStockAt} optionalLabel={copy.optionalShort} error={editProductErrors.lowStockAt}>
                  <Input
                    data-testid="sell-edit-product-low-stock"
                    placeholder="0"
                    inputMode="numeric"
                    value={editProductDraft.lowStockAt}
                    onChange={(e) => updateEditProductDraft("lowStockAt", e.target.value.replace(/[^0-9]/g, ""))}
                    className="h-10 rounded-lg border-border/70 bg-background font-mono text-[12px] font-semibold tabular-nums shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </PremiumField>
              </div>
              <PremiumField label={copy.optionalBarcode} optional optionalLabel={copy.optionalShort} error={editProductErrors.barcode}>
                <button
                  type="button"
                  data-testid="sell-edit-scan-barcode"
                  onClick={() => {
                    toast.info(copy.scannerReady, { description: copy.scannerReadyDescription });
                    document.querySelector("[data-testid='sell-search']")?.focus();
                  }}
                  className="flex min-h-[66px] w-full items-center gap-3 rounded-lg border border-border/70 bg-muted/30 px-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-0"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background text-primary shadow-sm">
                    <ScanBarcode className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] font-medium">{copy.optionalBarcode}</span>
                    <span className="block truncate text-[12px] text-muted-foreground">
                      {editProductDraft.barcode ? editProductDraft.barcode : copy.scanToAttach}
                    </span>
                  </span>
                </button>
              </PremiumField>
              <DialogFooter className="mt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeEditProductDialog}
                  disabled={isUpdatingProduct}
                  className="h-9"
                >
                  {copy.cancel}
                </Button>
                <Button
                  type="button"
                  data-testid="sell-edit-product-save"
                  onClick={saveEditedProduct}
                  disabled={isUpdatingProduct}
                  className="h-9 justify-center gap-2"
                >
                  {isUpdatingProduct ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
                  {isUpdatingProduct ? copy.saving : inventoryCopy.editProduct}
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResponsiveCheckout({ cartCount, cartLabel, children, compact, formattedTotal, onOpenChange, open }) {
  if (!compact) {
    return (
      <aside className="flex h-full w-[340px] max-w-[340px] shrink-0 flex-col border-l border-border bg-card text-[12px]">
        {children}
      </aside>
    );
  }

  return (
    <>
      <Button
        data-testid="sell-open-checkout"
        type="button"
        variant={cartCount > 0 ? "default" : "outline"}
        onClick={() => onOpenChange(true)}
        className="fixed bottom-5 right-5 z-30 h-12 gap-2 rounded-full px-4 shadow-[0_18px_45px_-18px_rgba(15,23,42,0.55)]"
        aria-label={`${cartLabel}: ${cartCount}`}
      >
        <ShoppingBag className="h-4 w-4" />
        <span className="font-semibold">{cartLabel}</span>
        <Badge
          variant={cartCount > 0 ? "secondary" : "outline"}
          className="h-5 min-w-5 rounded-full px-1.5 font-mono text-[10px]"
        >
          {cartCount}
        </Badge>
        {cartCount > 0 ? (
          <span className="border-l border-current/20 pl-2 font-mono text-xs tabular-nums">
            {formattedTotal}
          </span>
        ) : null}
      </Button>

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[400px] [&_.checkout-panel-header]:pr-14"
        >
          <SheetTitle className="sr-only">{cartLabel}</SheetTitle>
          <div className="flex h-full min-h-0 flex-col bg-card text-[12px]">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function StockBadge({ copy, status, stock }) {
  const map = {
    in_stock: { c: "bg-success/15 text-success", l: copy.stockIn(stock) },
    low_stock: { c: "bg-warning/15 text-warning", l: copy.stockLeft(stock) },
    stock_out: { c: "bg-destructive/15 text-destructive", l: copy.stockOut },
    untracked: { c: "bg-muted text-muted-foreground", l: copy.untracked },
  };
  const v = map[status];
  return <span className={`shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${v.c}`}>{v.l}</span>;
}

function ProductCardMenu({ copy, disabled = false, onEdit, onRegenerateImage }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="sell-product-menu"
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/80 bg-background/95 text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-border/80 disabled:cursor-wait disabled:opacity-70"
          aria-label={copy.productMenu}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 outline-none ring-0 focus:outline-none">
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation();
            onRegenerateImage?.();
          }}
          className="gap-2"
        >
          <RefreshCcw className="h-4 w-4" />
          {copy.regenerateImage}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation();
            onEdit?.();
          }}
          className="gap-2"
        >
          <Pencil className="h-4 w-4" />
          {copy.editProduct}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
